import React, { useRef, useEffect } from 'react'
import TextField from '@mui/material/TextField'
import MuiAutocomplete from '@mui/material/Autocomplete'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { DateTime } from 'luxon'
import { useState } from 'react'

import { Text, Flex } from '..'
import { COLORS } from './designSystem'

const baseInputStyle = {
  width: '100%',
  borderRadius: '0px 6px 6px 0px',
  backgroundColor: COLORS.white,
  border: `1px solid ${COLORS.greyLight}`,
  paddingLeft: 12,
  fontFamily: 'Poppins, sans-serif',
  height: 42,
  textAlign: 'left'
}

const extractContainerStyle = (style = {}) => {
  const { width, height, marginTop, marginBottom, marginLeft, marginRight, gridArea, ...rest } = style
  return {
    container: { width, height, marginTop, marginBottom, marginLeft, marginRight, gridArea },
    inner: rest
  }
}

export const Input = ({
  setRef,
  setValue,
  useState: useStateProp,
  onChange,
  onEnter,
  autofocus,
  password,
  time,
  date,
  label,
  labelStyle,
  multiline,
  dropdown,
  inputAutoComplete,
  autocomplete,
  dropdownAutoComplete,
  error,
  ...props
}) => {
  const inputRef = useRef(null)
  const [showPassword, setShowPassword] = useState(false)
  if (setRef) setRef(inputRef)

  useEffect(() => {
    if (!autofocus) return
    setTimeout(() => {
      const el = inputRef.current
      if (!el) return
      el?.focus?.()
    }, 200)
  }, [autofocus])

  if (onChange && !props.id) {
    throw new Error('Missing ID for OnChange')
  }

  if (props.value === undefined) props.value = ''
  if (useStateProp?.length === 2) props.value = useStateProp[0]

  const handleChange = (e) => {
    const value = e.target.value
    if (useStateProp) useStateProp[1](value)
    if (setValue) setValue(value)
    if (onChange) onChange({ [props.id]: value })
  }

  const handleKeyDown = (key) => {
    if (onEnter && key.keyCode === 13) onEnter()
  }

  props.autoComplete = props.autoComplete || 'off'

  const { container, inner } = extractContainerStyle(props.style || {})
  const inputStyle = { ...baseInputStyle, ...inner }

  // -------------- DATE ----------------
  if (date) {
    const dateValue = props.value
      ? (props.value instanceof Date ? DateTime.fromJSDate(props.value) : DateTime.fromISO(props.value))
      : null

    return (
      <Flex row js style={{ backgroundColor: COLORS.white, paddingLeft: 8, borderRadius: 6, ...container }}>
        {label && <Text value={label} color={COLORS.textLight} />}
        <DatePicker
          inputRef={inputRef}
          value={dateValue}
          format='dd/MM/yyyy'
          onChange={(newValue) => {
            if (onChange) onChange({ [props.id]: newValue ? newValue.toJSDate() : null })
          }}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              error: !!error,
              sx: { '& .MuiOutlinedInput-root': { borderRadius: '6px' } }
            }
          }}
        />
      </Flex>
    )
  }

  // -------------- TIME ----------------
  if (time) {
    const timeValue = props.value && props.value !== 'Invalid Date'
      ? (props.value instanceof Date ? DateTime.fromJSDate(props.value) : DateTime.fromISO(props.value))
      : null

    return (
      <div style={{ position: 'relative', ...inputStyle, borderRadius: 6 }}>
        <TimePicker
          inputRef={inputRef}
          value={timeValue}
          ampm={false}
          onChange={(newValue) => {
            if (onChange) onChange({ [props.id]: newValue ? newValue.toJSDate() : null })
          }}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              label: label || undefined,
              error: !!error
            }
          }}
        />
      </div>
    )
  }

  // -------------- PASSWORD ----------------
  if (password) {
    return (
      <TextField
        inputRef={inputRef}
        type={showPassword ? 'text' : 'password'}
        value={props.value}
        id={props.id}
        placeholder={props.placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        fullWidth
        size='small'
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={() => setShowPassword(!showPassword)}
                  edge='end'
                  size='small'
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }
        }}
      />
    )
  }

  // -------------- MULTILINE ----------------
  if (multiline) {
    return (
      <TextField
        inputRef={inputRef}
        multiline
        minRows={3}
        value={props.value}
        id={props.id}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        fullWidth
        size='small'
        sx={{ '& .MuiOutlinedInput-root': { padding: '12px' } }}
      />
    )
  }

  // -------------- DROPDOWN ----------------
  if (dropdown) {
    const {
      options = [], optionValue, optionLabel, placeholder, showClear,
      itemTemplate, valueTemplate, disabled, value, id, className
    } = props

    const getOptionValue = (opt) => {
      if (typeof opt === 'string') return opt
      return optionValue ? opt[optionValue] : opt.value
    }

    const getOptionLabel = (opt) => {
      if (typeof opt === 'string') return opt
      return optionLabel ? opt[optionLabel] : opt.label
    }

    const handleDropdownChange = (e) => {
      const val = e.target.value
      if (onChange) onChange({ [id]: val })
    }

    if (label) {
      return (
        <Flex js fw row style={{ alignItems: 'stretch' }}>
          <Flex
            row
            style={{
              backgroundColor: COLORS.primary,
              paddingLeft: '5px',
              paddingRight: '5px',
              borderRadius: '6px 0 0 6px',
              width: 'fit-content',
              minWidth: 180,
              textAlign: 'left',
              ...(labelStyle || {})
            }}
          >
            <Text value={label} bold color={COLORS.white} style={{ whiteSpace: 'nowrap' }} />
          </Flex>
          <FormControl fullWidth size='small'>
            <Select
              value={value || ''}
              onChange={handleDropdownChange}
              displayEmpty
              disabled={disabled}
              renderValue={(selected) => {
                if (!selected && selected !== 0) return <em style={{ color: '#999' }}>{placeholder || ''}</em>
                if (valueTemplate) {
                  const opt = options.find(o => getOptionValue(o) === selected)
                  return valueTemplate(selected, opt)
                }
                const opt = options.find(o => getOptionValue(o) === selected)
                return opt ? getOptionLabel(opt) : selected
              }}
              sx={{
                borderRadius: '0 6px 6px 0',
                backgroundColor: '#F5F5F5'
              }}
            >
              {showClear && <MenuItem value=''><em>{placeholder || 'Tutto'}</em></MenuItem>}
              {options.map((opt, i) => (
                <MenuItem key={i} value={getOptionValue(opt)}>
                  {itemTemplate ? itemTemplate(typeof opt === 'string' ? opt : getOptionValue(opt)) : getOptionLabel(opt)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Flex>
      )
    }

    return (
      <FormControl fullWidth size='small' sx={{ ...container }}>
        <Select
          value={value || ''}
          onChange={handleDropdownChange}
          displayEmpty
          disabled={disabled}
          renderValue={(selected) => {
            if (!selected && selected !== 0) return <em style={{ color: '#999' }}>{placeholder || ''}</em>
            if (valueTemplate) {
              const opt = options.find(o => getOptionValue(o) === selected)
              return valueTemplate(selected, opt)
            }
            const opt = options.find(o => getOptionValue(o) === selected)
            return opt ? getOptionLabel(opt) : selected
          }}
          sx={{
            borderRadius: '6px',
            backgroundColor: '#F5F5F5'
          }}
        >
          {showClear && <MenuItem value=''><em>{placeholder || 'Tutto'}</em></MenuItem>}
          {options.map((opt, i) => (
            <MenuItem key={i} value={getOptionValue(opt)}>
              {itemTemplate ? itemTemplate(typeof opt === 'string' ? opt : getOptionValue(opt)) : getOptionLabel(opt)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  // -------------- AUTOCOMPLETE ----------------
  if (inputAutoComplete) {
    const { suggestions = [], completeMethod, itemTemplate: acItemTemplate, onSelect, id: acId, className } = props

    return (
      <Flex row js style={{ backgroundColor: COLORS.primary, paddingLeft: 12, borderRadius: 6, ...container }}>
        <Text value={label} bold color={COLORS.white} style={(labelStyle || {})} />
        <MuiAutocomplete
          freeSolo
          options={suggestions}
          inputValue={props.value || ''}
          onInputChange={(event, newValue, reason) => {
            if (reason === 'input') {
              if (onChange) onChange({ [acId]: newValue })
              if (completeMethod) completeMethod({ query: newValue })
            }
          }
          }
          onChange={(event, newValue) => {
            if (newValue && onSelect) onSelect({ value: newValue })
          }}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option
            return option?.name || option?.label || ''
          }}
          renderOption={(renderProps, option) => (
            <li {...renderProps} key={option?._id || option?.name || Math.random()}>
              {acItemTemplate ? acItemTemplate(option) : (option?.name || option?.label || String(option))}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0 6px 6px 0',
                  backgroundColor: '#F5F5F5'
                }
              }}
            />
          )}
          sx={{ width: '100%' }}
        />
      </Flex>
    )
  }

  // -------------- LABEL + INPUT ----------------
  if (label) {
    return (
      <Flex js fw row style={{ alignItems: 'stretch' }}>
        <Flex
          row
          style={{
            backgroundColor: COLORS.primary,
            paddingLeft: '5px',
            paddingRight: '5px',
            borderRadius: '6px 0 0 6px',
            width: 'fit-content',
            minWidth: 150,
            ...(labelStyle || {})
          }}
        >
          <Text value={label} bold color={COLORS.white} style={{ whiteSpace: 'nowrap' }} />
        </Flex>
        <TextField
          inputRef={inputRef}
          value={props.value}
          id={props.id}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          error={!!error}
          fullWidth
          size='small'
          placeholder={props.placeholder}
          disabled={props.disabled}
          autoComplete={props.autoComplete}
          aria-label={props['aria-label']}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '0 6px 6px 0'
            }
          }}
        />
      </Flex>
    )
  }

  // -------------- DEFAULT INPUT ----------------
  return (
    <TextField
      inputRef={inputRef}
      value={props.value}
      id={props.id}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      error={!!error}
      className={error ? 'invalid-input' : props.className}
      fullWidth
      size='small'
      placeholder={props.placeholder}
      disabled={props.disabled}
      autoComplete={props.autoComplete}
      aria-label={props['aria-label']}
      sx={props.sx}
    />
  )
}
